import { useState, useEffect } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { motion } from 'framer-motion';
import { Sheet, SheetContent } from '@/components/ui/sheet';
import { Button } from '@/components/ui/button';
import { Settings, LogIn, Info, Zap, ShieldCheck, Smartphone } from 'lucide-react';
import { ThemeToggle } from '@/components/common/ThemeToggle';
import { useTheme } from '@/hooks/useTheme';
import { UserMenu } from '@/components/common/UserMenu';
import { ConnectionStatus } from '@/components/common/ConnectionStatus';
import { SettingsDialog } from '@/components/common/SettingsDialog';
import { useAuthStore } from '@/lib/stores/authStore';
import { auth } from '@/lib/firebase';
import { IS_DEV, DEV_TEST_EMAIL, DEV_TEST_PASSWORD } from '@/lib/constants';
import { toast } from '@/lib/toast';
import { useLobbyStore } from '@/lib/stores/lobbyStore';
import { apiClient } from '@/lib/api';

interface SidebarProps {
  isMobile?: boolean;
  isOpen: boolean;
  isDesktopExpanded: boolean;
  setIsOpen: (isOpen: boolean) => void;
}

type NavItem = { name: string; icon: typeof Info; path: string };

// Public navigation items (unauthenticated users)
const publicNavItems: NavItem[] = [{ name: 'About', icon: Info, path: '/about' }];

// Lobby navigation items - Settings is a modal button, not a route.
// Home navigation is handled via the Polymyth logo in the sidebar header.
const lobbyNavItems: NavItem[] = [];

// Game session navigation items
const gameNavItems: NavItem[] = [];

const DevLoginButton = ({ isCollapsed }: { isCollapsed: boolean }) => {
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleDevLogin = async () => {
    setLoading(true);
    try {
      await signInWithEmailAndPassword(auth, DEV_TEST_EMAIL, DEV_TEST_PASSWORD);
      useLobbyStore.getState().reset();
      apiClient.clearAll();
      toast.success('Dev login!');
      navigate('/dashboard', { replace: true });
    } catch {
      toast.error('Dev login failed');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Button
      variant="ghost"
      className={`w-full justify-start text-amber-600 hover:bg-amber-600/10 hover:text-amber-600 ${isCollapsed ? 'justify-center px-2' : ''}`}
      onClick={handleDevLogin}
      disabled={loading}
      title={isCollapsed ? 'Dev Login' : undefined}
    >
      <Zap className={`h-5 w-5 ${!isCollapsed ? 'mr-3' : ''}`} />
      {!isCollapsed && (loading ? 'Logging in...' : 'Dev Login')}
    </Button>
  );
};

function useIsAdmin(): boolean {
  const { isLoggedIn } = useAuthStore();
  const [isAdmin, setIsAdmin] = useState(false);

  useEffect(() => {
    if (!isLoggedIn) {
      setIsAdmin(false);
      return;
    }
    let cancelled = false;
    // Wait for Firebase to determine its auth state before calling protected
    // APIs. The Zustand store persists `isLoggedIn` to localStorage, so on a
    // page reload `isLoggedIn` rehydrates `true` BEFORE Firebase has restored
    // its session — `auth.currentUser` is null in that window. Calling
    // `getMyProfile` then sends no Authorization header (api.ts:getAuthHeaders
    // returns `{}` when currentUser is null), gets a 401, and the api layer
    // emits `session-expired`, polluting the dashboard with recoverable noise.
    // `auth.authStateReady()` resolves once Firebase has finished initial
    // auth restore; `getIdToken()` then either returns a real token or — if
    // the persisted login is actually stale — throws, which we swallow.
    void (async () => {
      try {
        await auth.authStateReady();
        if (cancelled || !auth.currentUser) return;
        await auth.currentUser.getIdToken();
        if (cancelled) return;
        const profile = await apiClient.getMyProfile();
        if (!cancelled) setIsAdmin(profile.isAdmin);
      } catch {
        // Non-fatal: admin gate stays false; user just doesn't see the
        // admin link. Real auth failures are handled upstream.
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isLoggedIn]);

  return isAdmin;
}

const SidebarContent = ({
  setIsOpen,
  isCollapsed,
  isGameMode,
  isAdmin,
  onOpenSettings,
}: {
  setIsOpen: (isOpen: boolean) => void;
  isCollapsed: boolean;
  isGameMode: boolean;
  isAdmin: boolean;
  onOpenSettings: () => void;
}) => {
  const { theme } = useTheme();
  const isDark = theme === 'dark';
  const { isLoggedIn } = useAuthStore();

  // Context-aware navigation
  const navItems = !isLoggedIn ? publicNavItems : isGameMode ? gameNavItems : lobbyNavItems;

  return (
    <div className="flex flex-col h-full text-sidebar-foreground">
      <div className="relative p-4 flex items-center justify-between">
        <div
          className="absolute bottom-0 left-0 right-0 h-px"
          style={{
            background:
              'linear-gradient(to right, transparent, hsl(var(--accent-gold) / 0.3), transparent)',
          }}
        />
        <Link
          to="/"
          onClick={() => !isCollapsed && setIsOpen(false)}
          className={isCollapsed ? 'mx-auto' : 'ml-2'}
          aria-label="Polymyth home"
        >
          {!isCollapsed ? (
            <img
              src={isDark ? '/polymyth_logo_v1_final_dark.png' : '/polymyth_logo_v1_final_lite.png'}
              alt="Polymyth"
              className="h-6 w-auto"
            />
          ) : (
            <span className="text-2xl text-accent-gold">☥</span>
          )}
        </Link>
      </div>

      <nav className="flex-1 p-2 space-y-1">
        {navItems.map((item) => (
          <Button
            key={item.name}
            asChild
            variant="ghost"
            className={`w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center px-2' : ''}`}
            onClick={() => !isCollapsed && setIsOpen(false)}
            title={isCollapsed ? item.name : undefined}
          >
            <Link to={item.path}>
              <item.icon className={`h-5 w-5 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && item.name}
            </Link>
          </Button>
        ))}
        {/* Admin link - only for admin users */}
        {isAdmin && (
          <Button
            asChild
            variant="ghost"
            className={`w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center px-2' : ''}`}
            onClick={() => !isCollapsed && setIsOpen(false)}
            title={isCollapsed ? 'Admin' : undefined}
          >
            <Link to="/admin">
              <ShieldCheck className={`h-5 w-5 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && 'Admin'}
            </Link>
          </Button>
        )}
        {/* Settings button - only for authenticated users */}
        {isLoggedIn && (
          <Button
            variant="ghost"
            className={`w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center px-2' : ''}`}
            onClick={onOpenSettings}
            title={isCollapsed ? 'Settings' : undefined}
          >
            <Settings className={`h-5 w-5 ${!isCollapsed ? 'mr-3' : ''}`} />
            {!isCollapsed && 'Settings'}
          </Button>
        )}
        {isLoggedIn && (
          <Button
            asChild
            variant="ghost"
            className={`w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center px-2' : ''}`}
            onClick={() => !isCollapsed && setIsOpen(false)}
            title={isCollapsed ? 'Play on Mobile' : undefined}
          >
            <Link to="/get-the-app">
              <Smartphone className={`h-5 w-5 ${!isCollapsed ? 'mr-3' : ''}`} />
              {!isCollapsed && 'Play on Mobile'}
            </Link>
          </Button>
        )}
      </nav>

      <div className="relative p-2 space-y-1">
        <div
          className="absolute top-0 left-0 right-0 h-px"
          style={{
            background:
              'linear-gradient(to right, transparent, hsl(var(--accent-gold) / 0.3), transparent)',
          }}
        />
        {/* Connection Status - only for authenticated users */}
        {isLoggedIn && (
          <div className={`${isCollapsed ? 'flex justify-center' : ''}`}>
            <ConnectionStatus isCollapsed={isCollapsed} />
          </div>
        )}

        {/* Theme Toggle */}
        <div className={`${isCollapsed ? 'flex justify-center' : ''}`}>
          <ThemeToggle isCollapsed={isCollapsed} />
        </div>

        {/* User Menu / Sign In / Dev Login */}
        {isLoggedIn ? (
          <div className={`${isCollapsed ? 'flex justify-center' : ''}`}>
            <UserMenu isCollapsed={isCollapsed} />
          </div>
        ) : (
          <>
            {IS_DEV && <DevLoginButton isCollapsed={isCollapsed} />}
            <Button
              asChild
              variant="ghost"
              className={`w-full justify-start text-sidebar-foreground hover:bg-sidebar-accent hover:text-sidebar-accent-foreground ${isCollapsed ? 'justify-center px-2' : ''}`}
              title={isCollapsed ? 'Sign In' : undefined}
            >
              <Link to="/login">
                <LogIn className={`h-5 w-5 ${!isCollapsed ? 'mr-3' : ''}`} />
                {!isCollapsed && 'Sign In'}
              </Link>
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

export const Sidebar = ({ isMobile, isOpen, isDesktopExpanded, setIsOpen }: SidebarProps) => {
  const location = useLocation();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const isAdmin = useIsAdmin();

  // Determine if we're in game mode (routes starting with /play)
  const isGameMode = location.pathname.startsWith('/play');

  const handleOpenSettings = () => {
    setSettingsOpen(true);
  };

  if (isMobile) {
    return (
      <>
        <Sheet open={isOpen} onOpenChange={setIsOpen}>
          <SheetContent side="left" className="p-0 w-64">
            <SidebarContent
              setIsOpen={setIsOpen}
              isCollapsed={false}
              isGameMode={isGameMode}
              isAdmin={isAdmin}
              onOpenSettings={handleOpenSettings}
            />
          </SheetContent>
        </Sheet>
        <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
      </>
    );
  }

  // Desktop Sidebar - hover to expand, overlays content
  const isDesktopCollapsed = !isDesktopExpanded;

  return (
    <>
      {/* Backdrop when expanded - clicking closes it */}
      {isDesktopExpanded && (
        <motion.div
          className="fixed inset-0 bg-black/30 z-40"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsOpen(false)}
        />
      )}
      <motion.div
        data-state={isDesktopExpanded ? 'open' : 'closed'}
        className={`flex flex-col h-screen fixed top-0 left-0 text-sidebar-foreground shadow-lg ${isDesktopExpanded ? 'z-50' : 'z-20'}`}
        style={{
          backgroundColor: 'hsl(var(--sidebar) / 0.5)',
          backdropFilter: 'blur(12px)',
          WebkitBackdropFilter: 'blur(12px)',
        }}
        animate={{ width: isDesktopExpanded ? 220 : 64 }}
        transition={{ duration: 0.2, ease: 'easeOut' }}
        onMouseEnter={() => setIsOpen(true)}
        onMouseLeave={() => setIsOpen(false)}
      >
        <SidebarContent
          setIsOpen={setIsOpen}
          isCollapsed={isDesktopCollapsed}
          isGameMode={isGameMode}
          isAdmin={isAdmin}
          onOpenSettings={handleOpenSettings}
        />
      </motion.div>
      <SettingsDialog open={settingsOpen} onOpenChange={setSettingsOpen} />
    </>
  );
};
